import { useState, useEffect, memo, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import './EditArticleModal.css';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { apiClient } from '../services/apiClient';
import type { Article } from '../types/Article';
import { KeywordInput } from './KeywordInput';

interface EditArticleModalProps {
  isOpen: boolean;
  article: Article;
  onClose: () => void;
  onSave: (article: Article) => Promise<void>;
  onDelete?: (article: Article) => Promise<void>;
}

export const EditArticleModal = memo(({ 
  isOpen, 
  onClose, 
  article, 
  onSave, 
  onDelete 
}: EditArticleModalProps) => {
  const [formData, setFormData] = useState({
    title: article.title || '',
    doi: article.doi || '',
    journal: article.journal || '',
    keywords: article.keywords || '',
    coverUrl: article.coverUrl || '',
    publishedDate: article.publishedDate || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  // Definir onCoverDrop antes de useDropzone
  const handleCoverUpload = useCallback(async (file: File) => {
    try {
      if (!file.type.startsWith('image/')) {
        throw new Error('Por favor, selecione apenas arquivos de imagem');
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB
        throw new Error('A imagem deve ter no máximo 5MB');
      }

      console.log('📤 Iniciando upload da capa:', {
        articleId: article.id,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      const formData = new FormData();
      formData.append('cover', file);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/articles/${article.id}/cover`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('[EditArticle] Erro na resposta:', {
          status: response.status,
          data
        });
        throw new Error(data.error || 'Erro ao fazer upload da capa');
      }

      console.log('✅ Capa atualizada com sucesso:', data);

      const newCoverUrl = `${data.coverUrl}?t=${Date.now()}`;
      setFormData(prev => ({
        ...prev,
        coverUrl: newCoverUrl
      }));

      return newCoverUrl;
    } catch (error) {
      console.error('[EditArticle] Erro ao fazer upload:', error);
      throw error;
    }
  }, [article.id]);

  const onCoverDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      setIsUploading(true);
      setError(null);

      const file = acceptedFiles[0];
      if (!file) return;

      await handleCoverUpload(file);
    } catch (error) {
      console.error('[EditArticle] Erro ao processar capa:', error);
      setError(error instanceof Error ? error.message : 'Erro ao atualizar capa');
    } finally {
      setIsUploading(false);
    }
  }, [handleCoverUpload]);

  // Configurar dropzone após definir onCoverDrop
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onCoverDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    },
    maxFiles: 1
  });

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    try {
      // Validar formato da data
      const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      if (!dateRegex.test(formData.publishedDate)) {
        setError('Data inválida. Use o formato DD/MM/YYYY');
        return;
      }

      // Enviar dados sem converter a data
      const dataToSend = {
        ...formData,
        publishedDate: formData.publishedDate, // Manter formato DD/MM/YYYY
        keywords: Array.isArray(formData.keywords) ? formData.keywords : [],
        coverUrl: formData.coverUrl || null
      };

      console.log('[EditArticle] Enviando dados:', dataToSend);

      const response = await apiClient.fetch(`/articles/${article.id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToSend)
      });

      await onSave(response);
      onClose();
    } catch (error) {
      console.error('[EditArticle] Erro ao salvar:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Erro ao salvar artigo');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      await onDelete?.(article);
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir o artigo. Por favor, tente novamente.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Atualizar o renderCoverSection para usar o dropzone
  const renderCoverSection = () => (
    <div className="cover-section">
      <div 
        {...getRootProps()} 
        className={`cover-dropzone ${isDragActive ? 'active' : ''} ${isUploading ? 'uploading' : ''}`}
      >
        <input {...getInputProps()} />
        {formData.coverUrl ? (
          <>
            <img 
              src={formData.coverUrl} 
              alt="Capa do artigo"
              className="cover-preview"
            />
            <div className="cover-overlay">
              {isUploading ? (
                <span>Enviando...</span>
              ) : (
                <span>Clique ou arraste para trocar a imagem</span>
              )}
            </div>
          </>
        ) : (
          <div className="cover-placeholder">
            {isUploading ? (
              <span>Enviando...</span>
            ) : (
              <span>Clique ou arraste uma imagem para a capa</span>
            )}
          </div>
        )}
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="edit-article-modal">
          <div className="modal-header">
            <h2>Editar Artigo</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>

          <div className="edit-form">
            {errorMessage && <div className="error-message">{errorMessage}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-layout">
                {renderCoverSection()}

                <div className="form-group">
                  <label htmlFor="title">Título</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="doi">DOI</label>
                  <input
                    type="text"
                    id="doi"
                    name="doi"
                    value={formData.doi}
                    onChange={e => setFormData(prev => ({ ...prev, doi: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="journal">Revista</label>
                  <input
                    type="text"
                    id="journal"
                    name="journal"
                    value={formData.journal}
                    onChange={e => setFormData(prev => ({ ...prev, journal: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="publishedDate">Data de Publicação</label>
                  <input
                    type="text"
                    id="publishedDate"
                    name="publishedDate"
                    value={formData.publishedDate}
                    onChange={e => {
                      const value = e.target.value;
                      
                      // Se estiver apagando, permite
                      if (value.length < (formData.publishedDate?.length || 0)) {
                        setFormData(prev => ({ ...prev, publishedDate: value }));
                        return;
                      }
                      
                      // Permite apenas números e /
                      const cleaned = value.replace(/[^\d/]/g, '');
                      
                      // Formata a data automaticamente
                      let formattedDate = cleaned;
                      
                      // Adiciona as barras automaticamente
                      if (cleaned.length >= 2 && !cleaned.includes('/')) {
                        formattedDate = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
                      }
                      if (cleaned.length >= 5 && formattedDate.split('/').length < 3) {
                        formattedDate = formattedDate.slice(0, 5) + '/' + formattedDate.slice(5);
                      }
                      
                      // Limita o ano a 4 dígitos
                      const parts = formattedDate.split('/');
                      if (parts.length === 3 && parts[2].length > 4) {
                        parts[2] = parts[2].slice(0, 4);
                        formattedDate = parts.join('/');
                      }
                      
                      // Valida a data
                      if (formattedDate.length === 10) {
                        const [day, month, year] = formattedDate.split('/');
                        const dayNum = parseInt(day);
                        const monthNum = parseInt(month);
                        const yearNum = parseInt(year);
                        
                        // Validações básicas
                        if (monthNum > 12) return;
                        if (dayNum > 31) return;
                        if (monthNum === 2 && dayNum > 29) return;
                        if ([4,6,9,11].includes(monthNum) && dayNum > 30) return;
                        
                        // Validação do ano
                        const currentYear = new Date().getFullYear();
                        if (yearNum < 1800 || yearNum > currentYear + 1) return;

                        // Validação adicional para verificar se a data é válida
                        const date = new Date(yearNum, monthNum - 1, dayNum);
                        if (
                          date.getFullYear() !== yearNum ||
                          date.getMonth() !== monthNum - 1 ||
                          date.getDate() !== dayNum
                        ) {
                          return;
                        }
                      }
                      
                      // Atualiza apenas se a data estiver no formato correto ou estiver incompleta
                      if (formattedDate.length <= 10) {
                        setFormData(prev => ({ 
                          ...prev, 
                          publishedDate: formattedDate 
                        }));
                      }
                    }}
                    onBlur={() => {
                      // Validação adicional ao perder o foco
                      if (formData.publishedDate && formData.publishedDate.length < 10) {
                        setFormData(prev => ({ ...prev, publishedDate: '' }));
                      }
                    }}
                    maxLength={10}
                    placeholder="DD/MM/YYYY"
                    pattern="\d{2}/\d{2}/\d{4}"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Palavras-chave</label>
                  <KeywordInput
                    keywords={formData.keywords || []}
                    onChange={keywords => setFormData(prev => ({ ...prev, keywords }))}
                  />
                </div>
              </div>

              <div className="modal-actions">
                {onDelete && (
                  <button
                    type="button"
                    className="delete-button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Excluindo...' : 'Excluir'}
                  </button>
                )}
                <button
                  type="submit"
                  className="save-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <DeleteConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirm}
          title="Excluir Artigo"
          message="Tem certeza que deseja excluir este artigo? Esta ação não pode ser desfeita."
        />
      )}
    </>
  );
});

EditArticleModal.displayName = 'EditArticleModal'; 